import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ConfigService } from '../common/config.service';
import { HttpClient } from '@angular/common/http';
import { SocketService } from '../common/socket.service';
import { CancelOrderPayload } from '../../models/api/booking.model';
import { SnackbarService } from '../common/snackbar.service';
import { LstorageService } from '../common/lstorage.service';
import { LSkeys } from '../../configs/app.constants';


interface LocationData {
  source: { name: string; lat: number; lng: number } | string;
  dest: { name: string; lat: number; lng: number } | string;
}

interface UserChatData {
  userId: number | string;
  user_detail_id: number | string;
  user_name: string;
  user_type_id: number | string;
  orderId: number | string;
  driverId: number | string;
  driver_user_detail_id: number | string;
  driver_name: string;
}
interface UserHelperChatData {
  userId: number | string;
  user_detail_id: number | string;
  user_name: string;
  orderId: number | string;
  helperId: number | string;
  helper_user_detail_id: number | string;
  helper_name: string;
}


@Injectable({
  providedIn: 'root'
})

export class BookingSyncService {
  locationDisplay: string = '';
  private searchingOrderStatus$ = new BehaviorSubject<boolean>(false);
  private currentBlockSubject = new BehaviorSubject<string>('first');
  currentBlock$ = this.currentBlockSubject.asObservable();

  private unreadChatCounts: { [orderId: number]: number } = {};
  private unreadChatCountsSubject = new BehaviorSubject<{ [orderId: number]: number }>({});

  private unreadHelperChatCounts: { [orderId: number]: number } = {};
  private unreadHelperChatCountsSubject = new BehaviorSubject<{ [orderId: number]: number }>({});

  private activeChatOrderId: number | null = null;
  private activeHelperChatOrderId: number | null = null;

  private currentOrders: any[] = [];

  constructor(
    private configServ: ConfigService,
    private http: HttpClient,
    private socketServ: SocketService,
    private snackbarServ: SnackbarService,
    private lstorageServ: LstorageService
  ) {
    this.socketServ.message$.subscribe((data: any) => {
      this.handleIncomingMessage(data);
    });
  }

  setCurrentOrders(orders: any[]) {
    this.currentOrders = orders;
  }

  getUnreadChatCounts(): Observable<{ [orderId: number]: number }> {
    return this.unreadChatCountsSubject.asObservable();
  }

  getUnreadHelperChatCounts(): Observable<{ [orderId: number]: number }> {
    return this.unreadHelperChatCountsSubject.asObservable();
  }

  setActiveChat(orderId: number | null) {
    this.activeChatOrderId = orderId;
    if (orderId !== null) {
      this.clearUnreadChatCount(orderId);
    }
  }

  setActiveHelperChat(orderId: number | null) {
    this.activeHelperChatOrderId = orderId;
    if (orderId !== null) {
      this.clearUnreadHelperChatCount(orderId);
    }
  }

  setUnreadChatCounts(
    counts: { [orderId: number]: number }
  ): void {

    this.unreadChatCounts = {
      ...counts
    };

    this.unreadChatCountsSubject.next({
      ...this.unreadChatCounts
    });
  }

  incrementUnreadChatCount(orderId: number) {
    if (this.activeChatOrderId === orderId) return;
    this.unreadChatCounts[orderId] = (this.unreadChatCounts[orderId] || 0) + 1;
    this.unreadChatCountsSubject.next(this.unreadChatCounts);
  }

  clearUnreadChatCount(orderId: number) {
    this.unreadChatCounts[orderId] = 0;
    this.unreadChatCountsSubject.next(this.unreadChatCounts);
  }

  incrementUnreadHelperChatCount(orderId: number) {
    if (this.activeHelperChatOrderId === orderId) return;
    this.unreadHelperChatCounts[orderId] = (this.unreadHelperChatCounts[orderId] || 0) + 1;
    this.unreadHelperChatCountsSubject.next(this.unreadHelperChatCounts);
  }

  clearUnreadHelperChatCount(orderId: number) {
    this.unreadHelperChatCounts[orderId] = 0;
    this.unreadHelperChatCountsSubject.next(this.unreadHelperChatCounts);
  }

  handleIncomingMessage(data: any) {
    const orderId = +data.order_id;
    const sendBy = +data.send_by;
    const currentUserId = +(this.lstorageServ.getItem(LSkeys.USER_ID) || 0);
    if (sendBy === currentUserId) return;

    const matchedOrder = this.currentOrders.find(order => order.order_id && +order.order_id === orderId);

    if (matchedOrder) {
      const driverUserId = matchedOrder.driver_user_id ? +matchedOrder.driver_user_id : null;
      const helperUserId = matchedOrder.helper_user_id ? +matchedOrder.helper_user_id : null;

      const isDriverMessage = driverUserId && sendBy === driverUserId;
      const isHelperMessage = helperUserId && sendBy === helperUserId;

      if (isDriverMessage) {
        if (this.activeChatOrderId !== orderId) {
          this.snackbarServ.showInfo("New message received from driver.");
        }
        this.incrementUnreadChatCount(orderId);
      } else if (isHelperMessage) {
        if (this.activeHelperChatOrderId !== orderId) {
          this.snackbarServ.showInfo("New message received from helper.");
        }
        this.incrementUnreadHelperChatCount(orderId);
      } else {
        this.incrementUnreadChatCount(orderId);
      }
    } else {
      this.snackbarServ.showInfo("New message received.");
      this.incrementUnreadChatCount(orderId);
    }
  }

  private locationObj$ = new BehaviorSubject<LocationData>({ source: '', dest: '' });
  private isHelperServiceSubject = new BehaviorSubject<boolean>(false);
  private userChatObj$ = new BehaviorSubject<UserChatData>({
    userId: '',
    user_detail_id: '',
    user_type_id: '',
    user_name: '',
    orderId: '',
    driverId: '',
    driver_user_detail_id: '',
    driver_name: ''
  });

  private userHelperChatObj$ = new BehaviorSubject<UserHelperChatData>({
    userId: '',
    user_detail_id: '',
    user_name: '',
    orderId: '',
    helperId: '',
    helper_user_detail_id: '',
    helper_name: ''
  });

  setLocation(value: LocationData) {
    if (value.source && typeof value.source !== 'string') {
      if (typeof value.source.lat === 'number' && typeof value.source.lng === 'number') {
        if (!value.source.name) {
          this.locationDisplay = 'Enter Pickup Location...';
          value.source.name = '';
        } else {
          this.locationDisplay = value.source.name;
        }
        if (JSON.stringify(this.locationObj$.getValue()) !== JSON.stringify(value)) {
          this.locationObj$.next(value);
        }
      }
    } else if (typeof value.source === 'string') {
      this.locationDisplay = value.source;
    }
  }

  getLocation(): Observable<LocationData> {
    return this.locationObj$.asObservable();
  }

  cancelOrder(payload: CancelOrderPayload) {
    return this.http.post<any>(this.configServ.apiRoutes.CANCEL_ORDER, payload);
  }

  getUserDataForChat(): Observable<UserChatData | null> {
    return this.userChatObj$.asObservable();
  }
  getHelperDataForChat(): Observable<UserHelperChatData | null> {
    return this.userHelperChatObj$.asObservable();
  }

  setUserDataForChat(value: UserChatData): void {
    this.userChatObj$.next(value);
  }

  setHelperDataForChat(value: UserHelperChatData): void {
    this.userHelperChatObj$.next(value);
  }
  setHelperServiceMode(isHelper: boolean) {
    this.isHelperServiceSubject.next(isHelper);
  }

  getHelperServiceMode() {
    return this.isHelperServiceSubject.asObservable();
  }
  setCurrentBlock(block: string): void {
    this.currentBlockSubject.next(block);
  }

  getCurrentBlock(): Observable<string> {
    return this.currentBlock$;
  }
}
